import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        {/* Header */}
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8">
          <ArrowLeft size={18} />
          Torna alla Home
        </Link>

        <h1 className="text-4xl font-bold mb-2">Informativa sulla Privacy</h1>
        <p className="text-slate-400 mb-12">Ultimo aggiornamento: {new Date().toLocaleDateString('it-IT')}</p>

        <div className="space-y-8 text-slate-300">
          {/* 1. Introduzione */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Introduzione</h2>
            <p className="leading-relaxed">
              BudgetAI ("noi", "nostro", "noi", o "azienda") gestisce il sito web budgetai.tcdev.xyz (di seguito il "Servizio").
            </p>
            <p className="leading-relaxed mt-3">
              Questa pagina ti informa delle nostre politiche relative alla raccolta, all'uso e alla divulgazione di dati personali quando utilizzi il nostro Servizio e delle scelte che hai associato a questi dati.
            </p>
          </section>

          {/* 2. Definizioni */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Definizioni</h2>
            <ul className="space-y-3">
              <li>
                <strong>Servizio:</strong> Il sito web budgetai.tcdev.xyz e l'applicazione web ad esso collegata
              </li>
              <li>
                <strong>Dati Personali:</strong> Qualsiasi informazione concernente una persona fisica identificata o identificabile
              </li>
              <li>
                <strong>Dati di Utilizzo:</strong> Dati raccolti automaticamente durante l'uso del Servizio (es. indirizzo IP, tipo browser, pagine visitate)
              </li>
              <li>
                <strong>Cookie:</strong> Piccoli file di dati memorizzati sul tuo dispositivo
              </li>
            </ul>
          </section>

          {/* 3. Dati Personali Raccolti */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Dati Personali Raccolti</h2>
            
            <h3 className="text-xl font-semibold text-white mb-2">3.1 Dati Forniti da Te</h3>
            <ul className="space-y-2 ml-4 list-disc list-inside">
              <li>Email e password (per autenticazione)</li>
              <li>Nome completo (opzionale, per il profilo)</li>
              <li>Dati finanziari: spese, entrate, categorie (gestiti esclusivamente sul tuo account)</li>
              <li>Buste paga caricate (per estrazione dati AI)</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-2 mt-4">3.2 Dati Raccolti Automaticamente</h3>
            <ul className="space-y-2 ml-4 list-disc list-inside">
              <li>Indirizzo IP</li>
              <li>Tipo e versione browser</li>
              <li>Pagine visitate, ora di visita, durata</li>
              <li>Referrer e link su cui hai cliccato</li>
              <li>Log di accesso e attività (per sicurezza)</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-2 mt-4">3.3 Dati da Terze Parti</h3>
            <ul className="space-y-2 ml-4 list-disc list-inside">
              <li>Stripe: informazioni pagamento (non conserviamo carte di credito)</li>
              <li>Supabase Auth: profilo utente autenticato</li>
            </ul>
          </section>

          {/* 4. Uso dei Dati */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Utilizzo dei Dati</h2>
            <p className="leading-relaxed mb-3">Utilizziamo i tuoi dati per:</p>
            <ul className="space-y-2 ml-4 list-disc list-inside">
              <li>Fornire, mantenere e migliorare il Servizio</li>
              <li>Processare transazioni di pagamento (via Stripe)</li>
              <li>Inviare notifiche di ordine, aggiornamenti di account, comunicazioni di supporto</li>
              <li>Monitorare e analizzare l'utilizzo del Servizio (metriche aggregate, **mai dati personali**)</li>
              <li>Rilevare problemi di sicurezza e prevenire frodi</li>
              <li>Conformarsi agli obblighi legali</li>
            </ul>
            <p className="leading-relaxed mt-4 text-slate-400">
              <strong>Nota importante:</strong> I tuoi dati finanziari (spese, entrate, categorie) NON vengono mai utilizzati per scopi di advertising, vendita, profiling o condivisione. Rimangono strettamente privati e legati al tuo account.
            </p>
          </section>

          {/* 5. IA e Elaborazione Dati */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. IA e Elaborazione Dati</h2>
            <h3 className="text-xl font-semibold text-white mb-2">5.1 Categorizzazione IA</h3>
            <p className="leading-relaxed">
              Se utilizzi la funzionalità di categorizzazione AI (Premium), i tuoi dati di spesa vengono elaborati da modelli di IA per suggerire categorie. Questi dati rimangono sul nostro server e non vengono memorizzati dai fornitori di IA.
            </p>

            <h3 className="text-xl font-semibold text-white mb-2 mt-4">5.2 Estrazione Busta Paga</h3>
            <p className="leading-relaxed">
              Se carichi buste paga, i dati estratti (reddito, tasse) vengono elaborati localmente o su server sicuri. I file originali possono essere eliminati automaticamente dopo 30 giorni a meno che non li conservi manualmente.
            </p>

            <h3 className="text-xl font-semibold text-white mb-2 mt-4">5.3 Trasparenza</h3>
            <p className="leading-relaxed">
              Non addestriamo modelli di IA con i tuoi dati personali. Utilizziamo modelli pre-addestrati da provider verificati.
            </p>
          </section>

          {/* 6. Sicurezza */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Sicurezza dei Dati</h2>
            <ul className="space-y-2 ml-4 list-disc list-inside">
              <li>Crittografia SSL/TLS per dati in transito</li>
              <li>Crittografia a riposo</li>
              <li>Security gestito dal database: solo tu accedi ai tuoi dati</li>
              <li>Nessuno dei nostri admin può leggere i tuoi dati finanziari (architettura zero-knowledge per i dati sensibili)</li>
              <li>Backup regolari criptati</li>
              <li>Compliance con GDPR (diritti di accesso, cancellazione, portabilità)</li>
            </ul>
          </section>

          {/* 7. Condivisione Dati */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Condivisione dei Dati</h2>
            <p className="leading-relaxed mb-3">
              I tuoi dati personali non vengono mai venduti, affittati o condivisi con terze parti tranne nei seguenti casi:
            </p>
            <ul className="space-y-2 ml-4 list-disc list-inside">
              <li><strong>Stripe:</strong> Per processare pagamenti (solo email e piano scelto)</li>
              <li><strong>Supabase:</strong> Per hosting database autenticato (custode dati, non proprietario)</li>
              <li><strong>Obblighi legali:</strong> Se richiesto da legge, mandato, o ordine di tribunale</li>
            </ul>
          </section>

          {/* 8. Diritti Utente */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. I Tuoi Diritti (GDPR/CCPA)</h2>
            <p className="leading-relaxed mb-3">Hai il diritto di:</p>
            <ul className="space-y-2 ml-4 list-disc list-inside">
              <li><strong>Accesso:</strong> Richiedere una copia dei tuoi dati personali</li>
              <li><strong>Rettifica:</strong> Correggerli se inesatti</li>
              <li><strong>Cancellazione:</strong> Eliminare i tuoi account e dati (eseguibili tramite Dashboard)</li>
              <li><strong>Portabilità:</strong> Esportare i tuoi dati in formato standard (CSV)</li>
              <li><strong>Revoca del Consenso:</strong> Ritirare il consenso agli usi opzionali</li>
            </ul>
            <p className="leading-relaxed mt-4">
              Per esercitare questi diritti, contattaci a: <strong>privacy@budgetai.app</strong>
            </p>
          </section>

          {/* 9. Retention */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Conservazione dei Dati</h2>
            <ul className="space-y-2 ml-4 list-disc list-inside">
              <li><strong>Dati Account:</strong> Conservati finché l'account è attivo. Puoi eliminarli in qualsiasi momento</li>
              <li><strong>Dati Finanziari:</strong> Eliminati immediatamente alla cancellazione dell'account</li>
              <li><strong>Log di Accesso:</strong> Conservati per 90 giorni per motivi di sicurezza</li>
              <li><strong>Backup:</strong> Mantenuti per 30 giorni per disaster recovery</li>
            </ul>
          </section>

          {/* 10. Cookie */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Cookie</h2>
            <p className="leading-relaxed mb-3">
              Utilizziamo cookie per:
            </p>
            <ul className="space-y-2 ml-4 list-disc list-inside">
              <li><strong>Autenticazione:</strong> Mantenere la tua sessione accesa</li>
              <li><strong>Preferenze:</strong> Ricordare impostazioni (tema, lingua)</li>
              <li><strong>Analitiche:</strong> Capire come usi l'app (non per tracking personale)</li>
            </ul>
            <p className="leading-relaxed mt-4">
              Puoi controllare i cookie dal tuo browser. Disabilitarli potrebbe limitare la funzionalità.
            </p>
          </section>

          {/* 11. Modifiche */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Modifiche a Questa Informativa</h2>
            <p className="leading-relaxed">
              Potremmo aggiornare questa informativa periodicamente. Te ne notificheremo tramite email per modifiche significative. L'uso continuato del Servizio implica accettazione della versione aggiornata.
            </p>
          </section>

          {/* 12. Contatti */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Contattaci</h2>
            <p className="leading-relaxed">
              Se hai domande su questa informativa, contattaci a:
            </p>
            <ul className="space-y-2 ml-4 list-disc list-inside mt-3">
              <li><strong>Email:</strong> privacy@budgetai.app</li>
              <li><strong>Sito:</strong> https://budgetai.tcdev.xyz</li>
            </ul>
          </section>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-800 mt-12 pt-8 text-center text-sm text-slate-500">
          <p>© {new Date().getFullYear()} BudgetAI. Tutti i diritti riservati.</p>
        </div>
      </div>
    </div>
  );
}
